import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { API_BASE_URL } from '@/lib/api';
import { usePublicContent } from '@/lib/content';

const HERO_BG = "https://images.pexels.com/photos/7562087/pexels-photo-7562087.jpeg";

export default function LandingPage() {
  const navigate = useNavigate();
  const [inviteCode, setInviteCode] = useState('');
  const [liveState, setLiveState] = useState(null);
  const [timeTheme] = useState(() => getTimeTheme(new Date()));
  const content = usePublicContent();
  const landing = content.landing;

  const handleInviteSubmit = (e) => {
    e.preventDefault();
    if (inviteCode.trim()) {
      navigate(`/register/${encodeURIComponent(inviteCode.trim().toUpperCase())}`);
    }
  };

  useEffect(() => {
    let cancelled = false;

    const loadLiveState = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/live`);
        if (!response.ok) return;
        const data = await response.json();
        if (!cancelled) setLiveState(data);
      } catch {
        // Landing page visuals must never block guest access.
      }
    };

    loadLiveState();
    const interval = window.setInterval(loadLiveState, 45000);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, []);

  const musicMood = useMemo(() => {
    const nowPlaying = liveState?.music?.now_playing;
    if (nowPlaying?.is_playing) return 'playing';
    if (liveState?.music?.next_in_queue) return 'queued';
    return 'idle';
  }, [liveState]);

  const eventMode = useMemo(() => {
    const checkin = liveState?.checkin?.checked_in || 0;
    const barActive = (liveState?.bar?.pending || 0) + (liveState?.bar?.preparing || 0) + (liveState?.bar?.ready || 0);
    const musicActive = (liveState?.music?.queued || 0) + (liveState?.music?.played || 0);
    return checkin > 0 || barActive > 0 || musicActive > 0 ? 'active' : 'calm';
  }, [liveState]);

  return (
    <div className="landing-page min-h-screen bg-[#020617] relative overflow-hidden" data-time-theme={timeTheme} data-music-mood={musicMood} data-event-mode={eventMode}>
      <style>{landingStyles}</style>
      <div className="absolute inset-0 z-0">
        <img src={HERO_BG} alt="" className="w-full h-full object-cover opacity-10" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#020617]/80 via-[#020617]/92 to-[#020617]" />
        <div className="landing-aurora" aria-hidden="true" />
        <div className="landing-sheen" aria-hidden="true" />
      </div>

      <nav className="relative z-10 sticky top-0 bg-slate-900/80 backdrop-blur-xl border-b border-slate-800" data-testid="landing-nav">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between min-h-28 py-3">
          <img
            src="/logo.png"
            alt="Molin30"
            className="h-28 w-[28rem] max-w-[62vw] object-contain"
            data-testid="nav-logo"
          />

          <div className="flex items-center gap-2 sm:gap-4">
            <Button variant="ghost" className="text-slate-300 hover:text-white hover:bg-slate-800" onClick={() => navigate('/login')} data-testid="nav-guest-login">
              Gæstelogin
            </Button>

            <Button variant="ghost" className="text-slate-400 hover:text-slate-200 hover:bg-slate-800 text-sm hidden sm:flex" onClick={() => navigate('/admin/login')} data-testid="nav-admin-login">
              <Shield className="w-4 h-4 mr-1" /> Admin
            </Button>
          </div>
        </div>
      </nav>

      <main className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-40">
        <section className="flex flex-col items-center text-center">
          <div className="w-full max-w-3xl animate-fade-in">
            <p className="text-sm sm:text-base font-medium text-blue-300 mb-6">
              {landing.event_label}
            </p>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-slate-50 mb-6 leading-[1.1]" style={{ fontFamily: "'Outfit', sans-serif" }}>
              {renderLandingHeading(landing.heading)}
            </h1>

            <p className="text-base sm:text-lg text-slate-400 max-w-2xl mx-auto mb-10 leading-relaxed">
              {landing.description}
            </p>

            <form onSubmit={handleInviteSubmit} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto" data-testid="invite-form">
              <input
                type="text"
                placeholder={landing.invitation_placeholder}
                value={inviteCode}
                onChange={(e) => setInviteCode(e.target.value)}
                className="flex-1 bg-slate-900/50 border border-slate-700 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 text-slate-100 rounded-md px-4 py-3 placeholder:text-slate-500 outline-none transition-all"
                data-testid="invite-code-input"
              />

              <Button type="submit" className="bg-blue-600 hover:bg-blue-500 text-white font-medium px-6 py-3 rounded-md transition-colors" data-testid="invite-submit-btn">
                {landing.access_button_label}
              </Button>
            </form>
          </div>
        </section>
      </main>

      <footer className="relative z-10 border-t border-slate-800/40 py-8" aria-hidden="true" />
    </div>
  );
}

function renderLandingHeading(heading) {
  const text = heading || 'Velkommen til Molin30';
  const parts = text.split('Molin30');
  if (parts.length === 1) return text;
  return (
    <>
      {parts[0]}
      <span className="bg-gradient-to-r from-blue-400 via-indigo-400 to-purple-400 bg-clip-text text-transparent">Molin30</span>
      {parts.slice(1).join('Molin30')}
    </>
  );
}
function getTimeTheme(date) {
  const hour = date.getHours();
  if (hour >= 5 && hour < 11) return 'morning';
  if (hour >= 17 || hour < 5) return 'evening';
  return 'day';
}

const landingStyles = `
  .landing-page {
    --landing-blue: rgba(37, 99, 235, 0.16);
    --landing-purple: rgba(124, 58, 237, 0.14);
    --landing-cyan: rgba(14, 165, 233, 0.09);
    --landing-accent: rgba(59, 130, 246, 0.16);
  }

  .landing-page[data-time-theme="morning"] {
    --landing-blue: rgba(14, 165, 233, 0.14);
    --landing-purple: rgba(99, 102, 241, 0.11);
    --landing-cyan: rgba(45, 212, 191, 0.08);
  }

  .landing-page[data-time-theme="evening"] {
    --landing-blue: rgba(37, 99, 235, 0.13);
    --landing-purple: rgba(168, 85, 247, 0.17);
    --landing-cyan: rgba(99, 102, 241, 0.08);
  }

  .landing-page[data-music-mood="playing"] {
    --landing-accent: rgba(168, 85, 247, 0.2);
  }

  .landing-page[data-music-mood="queued"] {
    --landing-accent: rgba(59, 130, 246, 0.18);
  }

  .landing-page[data-event-mode="active"] {
    --landing-cyan: rgba(45, 212, 191, 0.1);
  }

  .landing-aurora {
    position: absolute;
    inset: -18% -10% auto -10%;
    height: 68%;
    background:
      radial-gradient(circle at 22% 28%, var(--landing-blue), transparent 34%),
      radial-gradient(circle at 72% 22%, var(--landing-purple), transparent 36%),
      radial-gradient(circle at 52% 66%, var(--landing-cyan), transparent 42%);
    filter: blur(36px);
    opacity: 0.82;
    transform: translate3d(0, 0, 0);
    animation: landingAurora 34s ease-in-out infinite alternate;
  }

  .landing-sheen {
    position: absolute;
    inset: 0;
    background:
      linear-gradient(115deg, transparent 12%, var(--landing-accent) 42%, transparent 72%),
      radial-gradient(circle at 50% 12%, rgba(255, 255, 255, 0.035), transparent 28%);
    opacity: 0.38;
    transform: translate3d(0, 0, 0);
    animation: landingSheen 42s ease-in-out infinite alternate;
  }

  @keyframes landingAurora {
    0% { transform: translate3d(-1.5%, -1%, 0) scale(1); }
    100% { transform: translate3d(1.5%, 1%, 0) scale(1.04); }
  }

  @keyframes landingSheen {
    0% { opacity: 0.22; transform: translate3d(-1%, 0, 0); }
    100% { opacity: 0.42; transform: translate3d(1%, 0.5%, 0); }
  }

  @media (prefers-reduced-motion: reduce) {
    .landing-aurora,
    .landing-sheen {
      animation: none;
    }
  }
`;
